import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormGroup, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { v4 as uuid } from 'uuid';
import { SupportingDocumentSection } from '../../models/SupportingDocumentSection.model';
import { ImageUtility } from '../../../../../../../utilities/ImageUtility';
import { SupportingDocumentComponent } from '../supporting-document/supporting-document.component';
import { NotificationService } from '../../../../../../../utilities/PortalNotificationUtility';

@Component({
  selector: 'app-supporting-document-section',
  templateUrl: './supporting-document-section.component.html',
  styleUrls: ['./supporting-document-section.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule, 
    MatIcon, 
    MatFormFieldModule,
    MatInputModule,
    SupportingDocumentComponent
  ]
})
export class SupportingDocumentSectionComponent implements OnInit {

  @ViewChild('documentInput') documentInput!: any;

  @Input() supportingDocumentSection!: SupportingDocumentSection;

  @Output() deleteEvent: EventEmitter<string> = new EventEmitter();
  @Output() sectionUpdatedEvent: EventEmitter<SupportingDocumentSection> = new EventEmitter();

  supportingDocumentForm!: FormGroup;

  constructor(
    private imageUtility: ImageUtility,
    private formBuilder: FormBuilder,
    private notificationService: NotificationService
  ) { }

  ngOnInit(): void {
    this.supportingDocumentForm = this.formBuilder.group({
      /*
        The description value is managed in a parent component (claim-form.component), so every time we re-render this component
        we need to make sure the description here syncs with the parent state
      */
      description: [this.supportingDocumentSection.description ?? '', { validators: [Validators.required] }],
    });

    this.supportingDocumentForm.valueChanges.subscribe(_ => {
      this.buildAndEmitUpdatedSection()
    });
  }

  onSelectDocument(event: Event) {
    const target = event.target as HTMLInputElement;
    Array.from(target.files as FileList).forEach(async document => {
      const executableExtensions = ['.asp', '.exe', '.dmg', '.bat', '.sh', '.cmd', '.com', '.msi', '.pkg', '.dll', '.ini'];
      const fileName = document.name;
      const fileExtension = fileName.substring(fileName.lastIndexOf('.')).toLowerCase();
      if (executableExtensions.includes(fileExtension.toLowerCase())) {
        this.notificationService.showNotification({ message: 'Invalid file type selected for upload' });
        this.onDeleteClick();
      }

      const maxSizeMB = 100;
      const maxSizeBytes = maxSizeMB * 1024 * 1024;
      if (document.size > maxSizeBytes) {
        this.notificationService.showNotification({ message: `Invalid file type, file exceeds limit of ${ maxSizeMB }MB.` });
        return;
      }

      const data = await this.imageUtility.getFileFromBlobAsync(document);
      this.addSupportingDocument(document, data);
      this.buildAndEmitUpdatedSection();
    });
  }

  onDeleteClick() {
    this.deleteEvent.emit(this.supportingDocumentSection.id);
  }

  private addSupportingDocument(document: File, result: string | ArrayBuffer | null) {
    this.supportingDocumentSection.documents.push({
      id: uuid(),
      fileKey: '',
      fileName: document.name,
      fileSize: document.size,
      fileData: result,
      fileType: document.type
    });
  }
  
  private buildAndEmitUpdatedSection() {
    this.supportingDocumentSection.description = this.supportingDocumentForm.controls['description'].value;
    this.sectionUpdatedEvent.emit();
  }
}